/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { AngularFireAction, DatabaseSnapshot } from '@angular/fire/compat/database';
import { BehaviorSubject, Observable, empty, of, from, EMPTY } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import {
	FancySupplementalAction,
	SupplementalActionTypes,
	CombinedSupplementalActions,
} from 'src/app/app-data/actions/supplemental-actions';

import { Swimlane, SwimlaneRenderTypes } from 'src/app/app-data/models/Swimlane';

import { DefaultActionHandlers } from '../../fancy-firebase/generics/DefaultActionHandlers';

import { GoldenColors } from '@touchplan/common';
import { FancyAction } from '../../fancy-firebase/base/FancyAction';
import { FancyFirebaseList } from '../../fancy-firebase/base/FancyFirebaseList';
import { FancyActionTypes } from '../../fancy-firebase/base/FancyActionTypes';
import { AngularLandConnection, RefOrObservable } from '../../fancy-firebase/base/interfaces';
import { monitorSubList } from '../../fancy-firebase/base/monitorSubList';
import { DefaultActionTypes } from '../../fancy-firebase/generics/DefaultActionTypes';
import { IConnectedCamera } from 'src/app/app-data/interfaces/connected-camera.interface';

const goldenColors = new GoldenColors(0.1);

export interface SwimlaneOccupiedSpace {
	top: number;
	bottom: number;
	center: number;
	overlapTop: number; //top - dragHeight/2
	overlapBottom: number; //bottom + dragHeight/2
}

export interface SwimlaneWithSelfObserver {
	swimlane: Swimlane;
	ob$: Observable<FancyAction<Swimlane>>;
}

export class SwimlaneList extends FancyFirebaseList<Swimlane, FancyAction> {
	private defaultActionsHandlers: DefaultActionHandlers<Swimlane, FancyAction, SwimlaneList> =
		new DefaultActionHandlers(this, [DefaultActionTypes.addItem]);
	public editList$: BehaviorSubject<Map<string, SwimlaneWithSelfObserver>> = new BehaviorSubject(new Map());
	private camera: IConnectedCamera;
	private range = { top: 0, bottom: 0 };

	protected addActions = {
		[FancyActionTypes.childAdded]: FancyActionTypes.childAdded,
		[DefaultActionTypes.addItem]: DefaultActionTypes.addItem,
	};
	/** ...also include removeItem as an "removeAction" */
	protected removeActions = {
		[FancyActionTypes.childRemoved]: FancyActionTypes.childRemoved,
		[DefaultActionTypes.removeItem]: DefaultActionTypes.removeItem,
	};

	constructor(angularLand: AngularLandConnection, path: RefOrObservable, fancyJoins?: any) {
		//fix any's
		super(angularLand, path, FancyAction, fancyJoins);
		this.logger.setContext('SwimlaneList');

		const obs = monitorSubList<Swimlane, SwimlaneWithSelfObserver>(
			this.rawEvent$,
			(action) => action.payload.currentRenderType === SwimlaneRenderTypes.edit,
			(type) => this.isRemoveAction(type),
			(action) => ({ swimlane: action.payload, ob$: this.getChildOb$(action.key) })
		);
		this.subscriptions.push(obs.subscribe(this.editList$));

		// this.rawEvent$.subscribe()

		this.logger.log('editList', this.editList$);
	}

	public $$added(action: AngularFireAction<DatabaseSnapshot<any>>) {
		const swim = new Swimlane(action.key);
		swim.camera = this.camera;
		swim.databaseUpdate(action.payload.val());
		return swim;
	}
	public $$updated(child: Swimlane, action: AngularFireAction<DatabaseSnapshot<any>>) {
		child.databaseUpdate(action.payload.val());
	}

	public handleCustomEvents(baseObservable) {
		return baseObservable.pipe(
			mergeMap((action: FancyAction) => {
				let item = this.defaultActionsHandlers.handle(action);

				switch (action.type) {
					//override the add event
					case DefaultActionTypes.addItem:
						item = this.handleAddItem(action);
						break;
					default:
						break;
				}
				if (!item) {
					return EMPTY;
				}
				if (item instanceof Observable) {
					return item;
				}
				return of(new FancyAction(item, action.type));
			})
		);
	}

	public handleSupplementalEvents(
		baseObservable: Observable<CombinedSupplementalActions>
	): Observable<FancySupplementalAction> {
		return baseObservable.pipe(
			mergeMap((action) => {
				switch (action.type) {
					case SupplementalActionTypes.cameraChanged:
						return this.cameraUpdate(action.payload);
					default:
						break;
				}

				return EMPTY;
			})
		);
	}

	protected handleAddItem(action: FancyAction) {
		const id = this.listRef.query.ref.push().key;
		const swim = this.makeEmptySwimlane(id);
		swim.switchViews(SwimlaneRenderTypes.edit);
		this._internalList.set(id, swim);

		const payload: any = swim.generateDatabaseOutput();
		this.listRef.set(id, payload);
		return swim;
	}

	public emptySwimlaneData() {
		const range = this.getRange();
		let top;
		if (!range.length && this.camera) {
			const worldRect = this.camera.globalView;
			top = worldRect.top + worldRect.height / 2;
		} else if (!range.length && !this.camera) {
			top = 0;
		} else {
			top = range[range.length - 1].bottom + 30;
		}

		return {
			top,
			bottom: top + 870,
			color: goldenColors.getRandomColor().rgba,
		};
	}

	public makeEmptySwimlane(id, data?) {
		const swim = new Swimlane(id);
		swim.camera = this.camera;
		if (data) {
			swim.databaseUpdate(data);
		} else {
			const data = this.emptySwimlaneData();
			swim.databaseUpdate(data);
		}
		return swim;
	}

	//temp method to get adds working, ripped from old code, can do better
	public getRange(record?) {
		const minSeperation = 5;
		//start inefficient:
		//- make new list with only positions
		//- sort
		const newArr = Array.from(this._internalList.values())
			.filter((rec) => rec !== record)
			.sort(function (a, b) {
				return a.view.top - b.view.top;
			})
			.map(function (s) {
				return { top: s.view.top, bottom: s.view.bottom };
			});

		for (let i = newArr.length - 1; i >= 0; i--) {
			const above = i ? newArr[i - 1] : null;
			const below = newArr[i];
			if (i && below.top - above.bottom < 5) {
				above.bottom = below.bottom;
				newArr.splice(i, 1);
			}
		}
		return newArr;
	}

	public getRange2(filterOut?: string[], filteredSize = 0, minSize = 5) {
		const filtered = {};
		if (filterOut) {
			filterOut.forEach((id) => (filtered[id] = id));
		}

		let filteredList: SwimlaneOccupiedSpace[] = [];
		this._internalList.forEach((swim, swimId) => {
			if (filtered[swimId]) {
				return;
			}
			// if(filteredList.length){
			// 	let lastItem = filteredList[filteredList.]
			// }

			filteredList.push({
				top: swim.view.top,
				bottom: swim.view.bottom,
				center: swim.view.top + (swim.view.bottom - swim.view.top) / 2,
				overlapTop: swim.view.top - filteredSize / 2,
				overlapBottom: swim.view.bottom + filteredSize / 2,
			});
		});
		filteredList = filteredList.sort(function (a, b) {
			return a.top - b.top;
		});

		for (let i = filteredList.length - 1; i >= 0; i--) {
			const above = i ? filteredList[i - 1] : null;
			const below = filteredList[i];
			if (i && below.top - above.bottom < minSize) {
				above.bottom = below.bottom;
				above.overlapBottom = above.bottom + filteredSize / 2;
				above.center = above.top + (above.bottom - above.top) / 2;
				filteredList.splice(i, 1);
			}
		}
		return filteredList;
	}

	//TODO - is probably worth genericizing this
	protected cameraUpdate(camera: IConnectedCamera) {
		this.camera = camera;
		const actionList = [];
		this._internalList.forEach((thing) => {
			thing.camera = camera;
			thing.recalcLocal();
			const act = this.constructAction(
				this.actionCtor,
				thing,
				SupplementalActionTypes.cameraChanged,
				thing.$id
			);
			actionList.push(act);
		});
		return from(actionList);
	}
}
